// The lift's own technical specification inputs, shared so neither surface that can capture one
// (the manual New report modal, the Gmail sweep review card) offers some of them and not others.
// Mirrors lolerPreContract.ts's reasoning exactly (Session ALH), applied to LOLER spec Item 1/2's
// five lift-spec fields (Session ALO) rather than the five pre-contract identity fields: with one
// builder and one field set there is no second place for the next one to go missing.
import type { ExtractedHeader, LolerReportCreateInput } from "../../lib/api";

// As typed into a form: always strings, never undefined, so a controlled input never flips between
// controlled and uncontrolled -- the same convention PreContractDetails uses.
export interface LiftSpecDetails {
  liftType: string;
  liftSerialNumber: string;
  liftFloors: string;
  liftInstallYear: string;
  liftAgeYears: string;
}

export const EMPTY_LIFT_SPEC: LiftSpecDetails = {
  liftType: "",
  liftSerialNumber: "",
  liftFloors: "",
  liftInstallYear: "",
  liftAgeYears: "",
};

// The five fields, in the order both surfaces render them. Kept as a list so the fields component
// and its tests walk the same set.
export const LIFT_SPEC_FIELDS = ["liftType", "liftSerialNumber", "liftFloors", "liftInstallYear", "liftAgeYears"] as const;

// Pre-fill from an extracted header. A field the model did not find stays empty for the office to
// type; nothing is ever guessed here (matches preContractFromHeader's own rule).
export function liftSpecFromHeader(h: ExtractedHeader | null | undefined): LiftSpecDetails {
  const src = h ?? {};
  return {
    liftType: src.liftType ?? "",
    liftSerialNumber: src.liftSerialNumber ?? "",
    liftFloors: src.liftFloors != null ? String(src.liftFloors) : "",
    liftInstallYear: src.liftInstallYear != null ? String(src.liftInstallYear) : "",
    liftAgeYears: src.liftAgeYears != null ? String(src.liftAgeYears) : "",
  };
}

export type LiftSpecCreateFields = Pick<LolerReportCreateInput, "liftType" | "liftSerialNumber" | "liftFloors" | "liftInstallYear" | "liftAgeYears">;

// Build the create/update-body fragment from typed strings: trims, drops blanks, and parses the
// three numeric fields. An unparseable or out-of-range value is dropped (undefined) rather than
// sent as NaN or a value the server would refuse -- the office simply never typed a usable number,
// which is the same as not having stated one. Pure.
export function buildLiftSpecFields(d: LiftSpecDetails): LiftSpecCreateFields {
  const floors = Number(d.liftFloors.trim());
  const installYear = Number(d.liftInstallYear.trim());
  const ageYears = Number(d.liftAgeYears.trim());
  return {
    liftType: d.liftType.trim() || undefined,
    liftSerialNumber: d.liftSerialNumber.trim() || undefined,
    liftFloors: d.liftFloors.trim() && Number.isInteger(floors) && floors >= 1 && floors <= 100 ? floors : undefined,
    liftInstallYear: d.liftInstallYear.trim() && Number.isInteger(installYear) && installYear >= 1900 && installYear <= 2100 ? installYear : undefined,
    liftAgeYears: d.liftAgeYears.trim() && Number.isInteger(ageYears) && ageYears >= 0 && ageYears <= 200 ? ageYears : undefined,
  };
}
